import {
  Box,
  Chip,
  Divider,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import ActionsScreen from '@/karehero-app/actions/ActionsScreen';
import KindeHostedSheet from '@/karehero-app/auth/KindeHostedSheet';
import WelcomeScreen from '@/karehero-app/auth/WelcomeScreen';
import Screen from '@/karehero-app/explore/Screen';
import Chat, { TalkInput } from '@/karehero-app/explore/Chat';
import { WAY_IN } from '@/karehero-app/explore/tell-us-once/TellUsOnce.content';

// Section A — the way in, in order: where the user arrives from (A1), who
// they tell us about (A2), and then the fork — the same eligibility questions
// asked two opposite ways (A3 and A4). A3 and A4 reach the same outcome by
// opposite means, so they deliberately share the question being asked and
// differ only in how it's put.

const noop = () => {};

// A1 — the real Kinde hosted set-password page, shown for reference so the
// fork after it is read in context rather than as a fresh start. Reuses the
// actual auth screens rather than a redrawing of them: this step is already
// built, and it is not what the exploration is questioning.
export const A1SetPassword = () => (
  <>
    <WelcomeScreen
      onCreateAccount={noop}
      onSignIn={noop}
      onForgotPassword={noop}
    />
    <KindeHostedSheet
      variant='register'
      initialStep='password'
      onClose={noop}
    />
  </>
);
A1SetPassword.displayName = 'A1SetPassword';

// A2 — the first thing asked once the account exists. About the person being
// cared for, not the carer, because everything downstream hangs off them.
export const A2WhoFor = () => {
  const c = WAY_IN.a2;
  return (
    <Screen
      title={c.title}
      footer={
        <Button ariaLabel={c.cta} isFullWidth>
          {c.cta}
        </Button>
      }
    >
      <Stack spacing={3}>
        <Typography variant='body1' color='text.secondary'>
          {c.body}
        </Typography>

        <TextField
          label={c.nameLabel}
          value={c.nameValue}
          size='small'
          fullWidth
        />
        <TextField
          label={c.relationLabel}
          value={c.relationValue}
          size='small'
          fullWidth
        />
      </Stack>
    </Screen>
  );
};
A2WhoFor.displayName = 'A2WhoFor';

const Option = ({ label, selected }: { label: string; selected?: boolean }) => (
  <Paper variant='outlined'>
    <Stack sx={{ alignItems: 'center', pr: 2 }} direction='row' spacing={1}>
      <Radio checked={!!selected} size='small' />
      <Typography sx={{ py: 1.5 }} variant='body2'>
        {label}
      </Typography>
    </Stack>
  </Paper>
);

// A3 — the calculator as a short branching form. The follow-up below the
// divider is the frame's whole argument: the answer opened it, and the
// branching logic is visible rather than implied.
export const A3ConditionalForm = () => {
  const c = WAY_IN.a3;
  return (
    <Screen
      title={c.question}
      subtitle={c.progress}
      back
      footer={
        <Button ariaLabel={c.cta} isFullWidth>
          {c.cta}
        </Button>
      }
    >
      <Stack spacing={3}>
        <Stack spacing={1}>
          {c.options.map((o) => (
            <Option key={o} label={o} selected={o === c.selected} />
          ))}
        </Stack>

        <Divider />

        <Stack spacing={1.5}>
          <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
            <Box sx={{ color: 'custom.limeGreen.800', display: 'flex' }}>
              <Icon icon={'chevrons-down' as never} fontSize={13} />
            </Box>
            <Chip size='small' label={c.branchNote} />
          </Stack>

          <Typography variant='h4'>{c.followUp}</Typography>

          <Stack spacing={1}>
            {c.followUpOptions.map((o) => (
              <Option key={o} label={o} />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Screen>
  );
};
A3ConditionalForm.displayName = 'A3ConditionalForm';

// A4 — the same outcome reached by talking. No progress bar and no visible
// branches, which is exactly the trust question the frame is there to raise.
export const A4Conversation = () => {
  const c = WAY_IN.a4;
  return (
    <Screen footer={<TalkInput typePrompt={c.typePrompt} state='listening' />}>
      <Stack sx={{ pt: 1 }} spacing={2}>
        <Chat turns={c.turns} />
      </Stack>
    </Screen>
  );
};
A4Conversation.displayName = 'A4Conversation';

// A5 — the care-circle invite as a conversation turn, not a form. It sits
// after the fork, so it has to be asked the way A4 asks everything else:
// said out loud, answered by typing, with an explicit way to park it. Drawn
// as a form screen it quietly contradicted the flow it lives in.
export const A5CareCircle = () => {
  const c = WAY_IN.a5;
  return (
    <Screen footer={<TalkInput typePrompt={c.typePrompt} state='listening' />}>
      <Stack sx={{ pt: 1 }} spacing={2}>
        <Chat turns={c.turns} />

        <Stack spacing={1}>
          {c.options.map((o) => (
            <Paper key={o.label} variant='outlined'>
              <Stack
                sx={{ alignItems: 'center', p: 1.5 }}
                direction='row'
                spacing={1.5}
              >
                <Box sx={{ color: 'custom.warmOrange.500', display: 'flex' }}>
                  <Icon icon={o.icon as never} fontSize={16} />
                </Box>
                <Typography sx={{ flex: 1, minWidth: 0 }} variant='body2'>
                  {o.label}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </Screen>
  );
};
A5CareCircle.displayName = 'A5CareCircle';

// A6 — the payoff. 'Likely' vs 'needs a check' is carried on the chip and in
// the note under each amount, which is the frame's real subject: showing what
// someone is owed without promising it.
export const A6Eligibility = () => {
  const c = WAY_IN.a6;
  return (
    <Screen
      footer={
        <Stack spacing={2}>
          <Button ariaLabel={c.cta} isFullWidth>
            {c.cta}
          </Button>
          <Button ariaLabel={c.secondary} variant='link' isFullWidth>
            {c.secondary}
          </Button>
        </Stack>
      }
    >
      <Stack sx={{ pt: 2 }} spacing={3}>
        <Typography variant='h3'>{c.lead}</Typography>

        <Stack spacing={1.5}>
          {c.benefits.map((b) => (
            <Paper key={b.name} variant='outlined'>
              <Stack sx={{ p: 2 }} spacing={1}>
                <Stack
                  sx={{
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                  }}
                  direction='row'
                  spacing={1}
                >
                  <Typography sx={{ flex: 1, minWidth: 0 }} variant='h5'>
                    {b.name}
                  </Typography>
                  <Chip
                    size='small'
                    label={
                      b.certainty === 'likely' ? 'Likely' : 'Needs a check'
                    }
                    color={
                      b.certainty === 'likely' ? 'limeGreen' : 'warmOrange'
                    }
                  />
                </Stack>

                <Typography variant='h4' color='custom.coral.700'>
                  {b.amount}
                </Typography>

                <Typography variant='caption' color='text.secondary'>
                  {b.note}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </Screen>
  );
};
A6Eligibility.displayName = 'A6Eligibility';

// A7 — where the parked invite lands: the real ActionsScreen, seeded with
// that one action instead of the stock demo set. Using the actual screen
// rather than a lookalike is the point — the question is whether deferring in
// conversation can land in the actions list we already have, so the frame has
// to show that list.
export const A7ParkedAction = () => (
  <ActionsScreen initial={{ actions: [WAY_IN.a7.action] }} />
);
A7ParkedAction.displayName = 'A7ParkedAction';
